import type { Tracker } from '../track';
import { Userpilot } from 'userpilot';
import { determinePlan, FREE_PLAN } from '../../../libs/purchase/plan';
import capitalize from 'lodash.capitalize';
import { getProviderName } from '../../../shared/getProviderName';
import { MembershipType } from '../../../services/room';

const UserpilotTracker: Tracker = {
  id: 'userpilot',

  identify: (
    user,
    institution,
    latestPurchase,
    rooms,
    currentPlan,
    userRole,
    authId,
    tenantId,
  ) => {
    // No point identifying if we don't know the user
    if (!user) {
      return;
    }

    // TODO: Use currentPlan instead of latestPurchase
    // This will need a complete refactor to reflect new application and billing logic
    // https://doxyme.atlassian.net/browse/PROD-4404
    const plan = latestPurchase
      ? determinePlan(latestPurchase.planId)
      : FREE_PLAN;

    const room = rooms?.find(
      (r) => r.node.membershipType === MembershipType.Owner,
    )?.node;

    // Userpilot.identify treats `typeof null === "object"` as unsupported unless the key is
    // `company`; omit null/undefined traits so we do not trigger their console warning.
    const userObject = {
      user_id: user.id,
      user_hash: user.hash,
      user_type: 'user',
      name: getProviderName({
        firstName: user.firstName,
        lastName: user.lastName,
      }),
      email: user.email,
      created_at: user.created,
      betaAccess: user.betaAccesses,
      ...(user.trialEndDate != null && { trialEnd_at: user.trialEndDate }),
      ...(plan.planId != null && { plan: plan.planId }),
      // Fixes broken data in Userpilot. Bigger rework still required of this file per the to-do above.
      ...(currentPlan && { plan_type: capitalize(currentPlan) }),
      ...(plan.currency != null && { plan_currency: plan.currency }),
      ...(plan.version != null && { plan_version: plan.version }),
      ...(plan.interval != null && { plan_period: plan.interval }),
      ...(room?.slug != null && room.slug !== '' && { roomSlug: room.slug }),
      ...(user.speciality != null && { specialty: user.speciality }),
      ...(authId != null && { auth_id: authId }),
      ...(tenantId != null && { tenant_id: tenantId }),
      ...(institution?.id != null && { institution_id: institution.id }),
      // NOTE: Institution is _always_ null in v2 as all Clinic customers are redirected to v1
      // this is left here so that if/when Clinic is activated on v2 then their details will
      // get sent to userpilot
      ...(userRole != null && { user_role: userRole }),
      ...(Boolean(institution?.id) && {
        company: {
          id: institution?.id,
          name: institution?.name,
          plan: plan.planId,
          created_at: institution?.created,
          seats: latestPurchase?.seats,
        },
      }),
    };

    Userpilot.identify(user.id.toString(), userObject);
  },

  track: async (event, properties) => {
    Userpilot.track(event, properties);
  },
};

export default UserpilotTracker;
